import { useMemo } from 'react';

import { useQueries } from '@tanstack/react-query';

import { contactsAPI, contactsEndpoints } from '@/api/contacts';
import type { ContactIdentity, IdentityConsent } from '@/shared/types/contacts';

/** The orchestrator returns consents as `{ rows }`; tolerate a bare array too. */
const toConsentRows = (data: unknown): IdentityConsent[] => {
  if (Array.isArray(data)) {
    return data as IdentityConsent[];
  }
  const rows = (data as { rows?: unknown; consents?: unknown } | undefined)?.rows;
  if (Array.isArray(rows)) {
    return rows as IdentityConsent[];
  }
  const consents = (data as { consents?: unknown } | undefined)?.consents;
  return Array.isArray(consents) ? (consents as IdentityConsent[]) : [];
};

/** An identity is treated as opted-out when it carries a revoked wildcard row. */
const hasRevokedWildcardRow = (rows: IdentityConsent[]): boolean =>
  rows.some((row) => row.channel_type === null && row.status === 'revoked');

/**
 * Edit-mode helper: reads the wildcard consent state across a contact's
 * email/phone identities. The consent checkbox starts checked unless any
 * identity carries a revoked wildcard row.
 */
export const useContactWildcardConsent = (
  contactId?: string,
  identities: ContactIdentity[] = []
) => {
  const reachableIdentities = useMemo(
    () =>
      identities.filter(
        (identity) =>
          (identity.type === 'email' || identity.type === 'phone') && !identity.deleted_at
      ),
    [identities]
  );

  const results = useQueries({
    queries: reachableIdentities.map((identity) => ({
      queryKey: [contactsEndpoints.consents(contactId ?? '', identity.identity_id)],
      queryFn: () => contactsAPI.listConsents(contactId!, identity.identity_id),
      enabled: !!contactId,
    })),
  });

  const isPending = results.some((result) => result.isPending);
  const hasError = results.some((result) => result.isError);
  // Only a successful query may uncheck the box — a failed request must not be
  // read as "no revoked row" and silently present an opted-out contact as opted in.
  const hasRevokedWildcard = results.some(
    (result) => result.isSuccess && hasRevokedWildcardRow(toConsentRows(result.data))
  );

  const getStatus = () => {
    if (isPending) {
      return 'pending' as const;
    }
    if (hasError) {
      return 'error' as const;
    }
    return 'success' as const;
  };

  return {
    hasRevokedWildcard,
    // "pending" until every per-identity query settles; "error" if any failed.
    status: getStatus(),
  };
};
